<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { callAPI, DRUPAL_API_ROUTE } from '@/service/DrupalService';
import WebformTemplatePreview from '@/components/WebformTemplatePreview.vue';

// Templates a new webform can start from: a search, the templates as cards
// showing two of their questions, and a full preview.
const { t } = useI18n();

const props = defineProps({
  visible: { type: Boolean, default: false },
  // Template selected when the picker opens.
  selected: { type: Object, default: null }
});
const emit = defineEmits(['update:visible', 'select']);

const templates = ref([]);
const loading = ref(false);
const current = ref(null);
const preview = ref(null);
const search = ref('');
const category = ref(null);

// Categories in their display order, the ones with templates only, and the
// number of templates in each.
const CATEGORIES = ['sales', 'marketing', 'hr', 'communication', 'admin', 'finance', 'management', 'purchasing',
  'logistics', 'maintenance', 'it', 'quality', 'production', 'legal', 'aftersales', 'travel', 'training',
  'environment', 'procedures'];
const categories = computed(() => CATEGORIES
  .map(value => ({ value, count: templates.value.filter(template => template.category === value).length }))
  .filter(item => item.count)
  .map(item => ({ value: item.value, label: `${t('webform.start.categories.' + item.value)} (${item.count})` })));

// Templates of the selected category matching the search, in their title,
// question titles or options, case and accents ignored.
const normalizeSearch = (text) => (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const filtered = computed(() => {
  const value = normalizeSearch(search.value.trim());
  const inCategory = category.value ? templates.value.filter(template => template.category === category.value) : templates.value;
  if (!value) return inCategory;
  return inCategory.filter(template =>
    [template.title, ...template.questions.flatMap(question => [question.title, ...(question.options ?? [])])]
      .some(text => normalizeSearch(text).includes(value)));
});

// Two questions shown in a template card: the ones that characterize the
// template first, personal data (name, email...) being alike in most of
// them, in their order in the form.
const cardQuestions = (template) => template.questions
  .map((question, index) => ({ question, index }))
  .sort((a, b) => Number(a.question.gdpr) - Number(b.question.gdpr) || a.index - b.index)
  .slice(0, 2)
  .sort((a, b) => a.index - b.index)
  .map(item => item.question);

const fetchTemplates = async () => {
  if (templates.value.length) return;
  loading.value = true;
  var request = await callAPI(DRUPAL_API_ROUTE.WEBFORM_TEMPLATES);
  templates.value = request.status == 200 ? request.data : [];
  loading.value = false;
}

watch(() => props.visible, (visible) => {
  if (!visible) return;
  current.value = props.selected;
  search.value = '';
  category.value = null;
  fetchTemplates();
});

const close = () => emit('update:visible', false);

const use = (template) => {
  preview.value = null;
  emit('select', template);
  close();
}
</script>

<template>
  <Dialog :visible="visible" @update:visible="(value) => emit('update:visible', value)" modal
    :header="t('webform.start.templates')" :closeOnEscape="false" class="w-[72rem] max-w-[calc(100vw-2rem)]"
    :pt="{ content: { style: 'padding-bottom: 0' }, footer: { style: 'padding-top: 1.25rem' } }">
    <div class="flex flex-col gap-2">
      <div class="sticky top-0 z-10 flex flex-col sm:flex-row gap-2 pb-3 bg-[var(--p-dialog-background)]">
        <IconField class="w-full sm:w-80">
          <InputIcon class="pi pi-search" />
          <InputText v-model="search" :placeholder="t('common.placeholders.search')" class="w-full" autocomplete="off" />
          <InputIcon v-if="search" class="pi pi-times cursor-pointer" role="button" tabindex="0"
            :aria-label="t('webform.start.clear')"
            @click="search = ''" @keydown.enter="search = ''" />
        </IconField>
        <Select v-model="category" :options="categories" optionLabel="label" optionValue="value" showClear
          :placeholder="t('webform.start.all_categories')" :loading="loading" class="w-full sm:w-72" />
      </div>
      <Message v-if="!loading && !filtered.length" severity="secondary" variant="simple"
        class="self-center my-6">{{ t('webform.start.no_results') }}</Message>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-2">
        <template v-if="loading">
          <Skeleton v-for="n in 4" :key="n" height="12rem" />
        </template>
        <template v-else>
          <div v-for="template in filtered" :key="template.id" role="button" tabindex="0"
            class="relative flex flex-col gap-3 p-4 rounded border-2 cursor-pointer"
            :class="current?.id === template.id ? 'border-primary bg-primary-50' : 'border-surface-200 hover:border-surface-300'"
            @click="current = template" @keydown.enter="current = template">
            <i v-if="current?.id === template.id" class="pi pi-check-circle text-primary absolute top-3 right-3"></i>
            <span class="font-bold pe-6">{{ template.title }}</span>
            <WebformTemplatePreview class="flex-1" :questions="cardQuestions(template)"
              :more="template.questions.length - cardQuestions(template).length" @more="preview = template" />
          </div>
        </template>
      </div>
    </div>
    <template #footer>
      <Button :label="t('common.labels.cancel')" severity="secondary" text @click="close" />
      <Button :label="t('webform.start.use')" :disabled="!current" @click="use(current)" />
    </template>
  </Dialog>
  <Dialog :visible="!!preview" @update:visible="preview = null" modal :header="preview?.title"
    class="w-[32rem] max-w-[calc(100vw-2rem)]">
    <WebformTemplatePreview v-if="preview" :questions="preview.questions" large />
    <template #footer>
      <Button :label="t('common.labels.close')" severity="secondary" text @click="preview = null" />
      <Button :label="t('webform.start.use')" @click="use(preview)" />
    </template>
  </Dialog>
</template>
